import { useState, useEffect, useMemo } from "react";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { ArrowLeft, Loader2, Grid3X3, AlertTriangle, Upload, FileDown, Network, GitBranch } from "lucide-react";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import jsPDF from "jspdf";
import autoTable from "jspdf-autotable";
import { useNavigate } from "react-router-dom";
import { ThemeToggle } from "@/components/ThemeToggle";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { PortfolioFileImport, ImportedPosition } from "@/components/PortfolioFileImport";
import { CorrelationClusterView } from "@/components/CorrelationClusterView";
import { CorrelationNetworkGraph } from "@/components/CorrelationNetworkGraph";
import { CorrelationDendrogram } from "@/components/CorrelationDendrogram";
import { Tooltip, TooltipContent, TooltipTrigger } from "@/components/ui/tooltip";

interface CorrelationResult {
  tickers: string[];
  matrix: number[][];
  period: string;
  dataPoints: number;
  skippedTickers: string[];
  sectorMap?: Record<string, string>;
}

function getCorrelationColor(value: number): string {
  // Strong negative = blue, zero = white/neutral, strong positive = red
  if (value >= 0.8) return "hsl(0, 80%, 45%)";
  if (value >= 0.6) return "hsl(0, 70%, 55%)";
  if (value >= 0.4) return "hsl(15, 65%, 60%)";
  if (value >= 0.2) return "hsl(30, 55%, 65%)";
  if (value > -0.2) return "hsl(0, 0%, 85%)";
  if (value > -0.4) return "hsl(210, 50%, 70%)";
  if (value > -0.6) return "hsl(210, 60%, 60%)";
  if (value > -0.8) return "hsl(220, 70%, 50%)";
  return "hsl(220, 80%, 40%)";
}

function getTextColor(value: number): string {
  if (Math.abs(value) >= 0.6) return "white";
  return "hsl(0, 0%, 20%)";
}

function correlationLabel(value: number): string {
  const abs = Math.abs(value);
  if (abs >= 0.8) return "Very Strong";
  if (abs >= 0.6) return "Strong";
  if (abs >= 0.4) return "Moderate";
  if (abs >= 0.2) return "Weak";
  return "Negligible";
}

export default function CorrelationMatrix() {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [user, setUser] = useState<any>(null);
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState<CorrelationResult | null>(null);
  const [period, setPeriod] = useState("6m");
  const [positions, setPositions] = useState<ImportedPosition[]>([]);
  const [showImport, setShowImport] = useState(false);

  useEffect(() => {
    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
    });
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      setUser(session?.user ?? null);
    });
    return () => subscription.unsubscribe();
  }, []);

  // Try to load positions from the latest portfolio snapshot
  useEffect(() => {
    if (!user) return;
    (async () => {
      const { data } = await supabase
        .from("portfolio_snapshots")
        .select("positions_data")
        .eq("user_id", user.id)
        .order("created_at", { ascending: false })
        .limit(1);
      if (data?.[0]?.positions_data) {
        const imported = (data[0].positions_data as any[])
          .map((p: any) => ({
            ticker: p.ticker || p.symbol || "",
            stockName: p.name || p.stock_name || p.stockName || p.ticker || "",
            quantity: p.quantity || 0,
            currentPrice: p.currentPrice || p.price_per_share || 0,
            totalValue: p.totalValue || p.total_value || p.localValue || 0,
            localCurrency: p.localCurrency || p.local_currency || p.currency || "USD",
            gainPct: p.gainPct || p.gain_pct || 0,
            initialInvestment: p.initialInvestment || p.initial_investment || 0,
            buyPrice: p.buyPrice || p.buy_price || 0,
          } as ImportedPosition))
          .filter((p) => p.ticker);
        if (imported.length > 0) setPositions(imported);
      }
    })();
  }, [user]);

  const tickers = useMemo(() => {
    return [...new Set(positions.map(p => p.ticker).filter(Boolean))];
  }, [positions]);

  const runCorrelation = async () => {
    if (tickers.length < 2) {
      toast({ title: "Need at least 2 positions", description: "Import your portfolio first.", variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      const { data, error } = await supabase.functions.invoke("portfolio-correlation", {
        body: { tickers: tickers.slice(0, 30), period },
      });
      if (error) throw error;
      if (data.error) throw new Error(data.error);
      setResult(data);
      if (data.skippedTickers?.length > 0) {
        toast({ title: "Some tickers skipped", description: `No data for: ${data.skippedTickers.join(", ")}` });
      }
    } catch (e: any) {
      toast({ title: "Correlation failed", description: e.message, variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const handleImport = (imported: ImportedPosition[], _source: 'excel' | 'ibkr') => {
    setPositions(imported);
    setShowImport(false);
    setResult(null);
    toast({ title: "Portfolio imported", description: `${imported.length} positions loaded` });
  };

  // Find top correlated & anti-correlated pairs
  const insights = useMemo(() => {
    if (!result) return null;
    const pairs: { a: string; b: string; corr: number }[] = [];
    for (let i = 0; i < result.tickers.length; i++) {
      for (let j = i + 1; j < result.tickers.length; j++) {
        pairs.push({ a: result.tickers[i], b: result.tickers[j], corr: result.matrix[i][j] });
      }
    }
    pairs.sort((a, b) => b.corr - a.corr);
    const mostCorrelated = pairs.slice(0, 3);
    const leastCorrelated = [...pairs].sort((a, b) => a.corr - b.corr).slice(0, 3);
    const avgCorr = pairs.length > 0 ? pairs.reduce((s, p) => s + p.corr, 0) / pairs.length : 0;

    // Sector concentration risk score (0-100)
    const sectorMap = result.sectorMap || {};
    const sectorCounts: Record<string, number> = {};
    result.tickers.forEach(t => {
      const s = sectorMap[t] || "Unknown";
      sectorCounts[s] = (sectorCounts[s] || 0) + 1;
    });
    const totalTickers = result.tickers.length;
    const sectorEntries = Object.entries(sectorCounts).sort((a, b) => b[1] - a[1]);
    const numSectors = sectorEntries.length;
    // HHI-based score: sum of squared shares, normalized to 0-100
    const hhi = sectorEntries.reduce((sum, [, count]) => sum + Math.pow(count / totalTickers, 2), 0);
    // Perfect diversification HHI = 1/numSectors, max HHI = 1
    // Scale: 0 = perfectly spread, 100 = all in one sector
    const sectorRiskScore = Math.round(((hhi - 1 / Math.max(numSectors, 1)) / (1 - 1 / Math.max(numSectors, 1))) * 100);
    const clampedScore = Math.max(0, Math.min(100, totalTickers <= 1 ? 0 : sectorRiskScore));
    const topSector = sectorEntries[0] ? { name: sectorEntries[0][0], pct: Math.round((sectorEntries[0][1] / totalTickers) * 100) } : null;

    return { mostCorrelated, leastCorrelated, avgCorr, sectorConcentration: { score: clampedScore, numSectors, topSector, sectorEntries } };
  }, [result]);

  const exportPDF = () => {
    if (!result || !insights) return;
    const doc = new jsPDF();
    const pageWidth = doc.internal.pageSize.getWidth();

    // Header
    doc.setFontSize(20);
    doc.setTextColor(26, 54, 93);
    doc.text("Portfolio Correlation Matrix", pageWidth / 2, 22, { align: "center" });
    doc.setFontSize(10);
    doc.setTextColor(102, 102, 102);
    doc.text("AddValueRotation Trading System", pageWidth / 2, 29, { align: "center" });
    const periodLabel = period === "3m" ? "3 Months" : period === "6m" ? "6 Months" : period === "1y" ? "1 Year" : "2 Years";
    doc.text("Period: " + periodLabel + "  |  Tickers: " + result.tickers.length + "  |  Data Points: " + result.dataPoints, pageWidth / 2, 35, { align: "center" });

    doc.setDrawColor(26, 54, 93);
    doc.setLineWidth(0.5);
    doc.line(14, 39, pageWidth - 14, 39);

    // Insights section
    let y = 47;
    doc.setFontSize(13);
    doc.setTextColor(26, 54, 93);
    doc.text("Key Insights", 14, y);
    y += 8;

    doc.setFontSize(10);
    doc.setTextColor(50, 50, 50);
    const diversNote = insights.avgCorr > 0.5
      ? "High average correlation - low diversification"
      : insights.avgCorr < 0.2
        ? "Well diversified portfolio"
        : "Moderate diversification";
    doc.text("Average Correlation: " + insights.avgCorr.toFixed(3) + " (" + diversNote + ")", 14, y);
    y += 6;
    const sc = insights.sectorConcentration;
    const scLabel = sc.score >= 70 ? "High concentration risk" : sc.score >= 40 ? "Moderate concentration" : "Well spread across sectors";
    doc.text("Sector Concentration Risk: " + sc.score + "/100 (" + scLabel + ") — " + sc.numSectors + " sectors" + (sc.topSector ? ", top: " + sc.topSector.name + " (" + sc.topSector.pct + "%)" : ""), 14, y);
    y += 8;

    doc.setFontSize(11);
    doc.setTextColor(26, 54, 93);
    doc.text("Most Correlated Pairs", 14, y);
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    insights.mostCorrelated.forEach((p) => {
      doc.text(p.a + " <-> " + p.b + ": " + p.corr.toFixed(3) + " (" + correlationLabel(p.corr) + ")", 18, y);
      y += 5;
    });
    y += 3;

    doc.setFontSize(11);
    doc.setTextColor(26, 54, 93);
    doc.text("Least Correlated (Hedges)", 14, y);
    y += 6;
    doc.setFontSize(9);
    doc.setTextColor(50, 50, 50);
    insights.leastCorrelated.forEach((p) => {
      doc.text(p.a + " <-> " + p.b + ": " + p.corr.toFixed(3) + " (" + correlationLabel(p.corr) + ")", 18, y);
      y += 5;
    });
    y += 6;

    // Correlation matrix table
    doc.setFontSize(13);
    doc.setTextColor(26, 54, 93);
    doc.text("Correlation Matrix", 14, y);
    y += 4;

    const headers = ["", ...result.tickers];
    const body = result.tickers.map((t, i) => [
      t,
      ...result.matrix[i].map((v, j) => i === j ? "1.00" : v.toFixed(2)),
    ]);

    autoTable(doc, {
      startY: y,
      head: [headers],
      body: body,
      styles: { fontSize: 6, cellPadding: 1.5, halign: "center" },
      headStyles: { fillColor: [26, 54, 93], fontSize: 6, cellPadding: 1.5 },
      columnStyles: { 0: { halign: "left", fontStyle: "bold" } },
      theme: "grid",
      didParseCell: (data: any) => {
        if (data.section === "body" && data.column.index > 0) {
          const val = parseFloat(data.cell.raw as string);
          if (!isNaN(val)) {
            if (val >= 0.6) {
              data.cell.styles.fillColor = [254, 202, 202];
              data.cell.styles.textColor = [127, 29, 29];
            } else if (val >= 0.3) {
              data.cell.styles.fillColor = [254, 232, 210];
            } else if (val <= -0.3) {
              data.cell.styles.fillColor = [191, 219, 254];
            } else if (val <= -0.6) {
              data.cell.styles.fillColor = [147, 197, 253];
              data.cell.styles.textColor = [30, 58, 138];
            }
          }
        }
      },
    });

    // Footer
    const finalY = (doc as any).lastAutoTable?.finalY || y + 40;
    doc.setFontSize(8);
    doc.setTextColor(130, 130, 130);
    doc.text("Generated on " + new Date().toLocaleString("en-GB"), pageWidth / 2, finalY + 10, { align: "center" });
    doc.text("AddValueRotation Trading System - Portfolio Correlation Analysis", pageWidth / 2, finalY + 15, { align: "center" });

    doc.save("Correlation_Matrix_" + new Date().toISOString().slice(0, 10) + ".pdf");
    toast({ title: "PDF exported", description: "Correlation matrix report downloaded." });
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-[1600px] mx-auto p-4 md:p-6 space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Button variant="ghost" size="icon" onClick={() => navigate("/")}>
              <ArrowLeft className="h-5 w-5" />
            </Button>
            <div>
              <h1 className="text-2xl font-bold tracking-tight flex items-center gap-2">
                <Grid3X3 className="h-6 w-6 text-primary" />
                Correlation Matrix
              </h1>
              <p className="text-sm text-muted-foreground">Portfolio position correlation heatmap</p>
            </div>
          </div>
          <ThemeToggle />
        </div>

        {/* Controls */}
        <Card>
          <CardContent className="p-4 flex flex-wrap items-center gap-4">
            <Button variant="outline" size="sm" onClick={() => setShowImport(!showImport)}>
              <Upload className="h-4 w-4 mr-1" /> {positions.length > 0 ? `${tickers.length} positions` : "Import Portfolio"}
            </Button>
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-32"><SelectValue /></SelectTrigger>
              <SelectContent>
                <SelectItem value="3m">3 Months</SelectItem>
                <SelectItem value="6m">6 Months</SelectItem>
                <SelectItem value="1y">1 Year</SelectItem>
                <SelectItem value="2y">2 Years</SelectItem>
              </SelectContent>
            </Select>
            <Button onClick={runCorrelation} disabled={loading || tickers.length < 2} className="gap-2">
              {loading && <Loader2 className="h-4 w-4 animate-spin" />}
              Calculate Correlations
            </Button>
            {result && (
              <>
                <Button variant="outline" size="sm" onClick={exportPDF} className="gap-1">
                  <FileDown className="h-4 w-4" /> Export PDF
                </Button>
                <Badge variant="secondary" className="text-xs">
                  {result.dataPoints} data points · {result.tickers.length} tickers
                </Badge>
              </>
            )}
          </CardContent>
        </Card>

        {/* Import Section */}
        {showImport && (
          <Card>
            <CardContent className="p-4">
              <PortfolioFileImport onImportComplete={handleImport} />
            </CardContent>
          </Card>
        )}

        {/* Insights */}
        {insights && result && (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Average Correlation</CardTitle>
              </CardHeader>
              <CardContent>
                <p className="text-3xl font-bold" style={{ color: getCorrelationColor(insights.avgCorr) }}>
                  {insights.avgCorr.toFixed(2)}
                </p>
                <p className="text-xs text-muted-foreground mt-1">
                  {insights.avgCorr > 0.5 ? "⚠️ High avg correlation — low diversification" : insights.avgCorr < 0.2 ? "✅ Well diversified portfolio" : "Moderate diversification"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Sector Concentration Risk</CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex items-end gap-2">
                  <p className="text-3xl font-bold" style={{
                    color: insights.sectorConcentration.score >= 70 ? "hsl(0, 80%, 50%)"
                      : insights.sectorConcentration.score >= 40 ? "hsl(35, 80%, 50%)"
                      : "hsl(142, 70%, 40%)"
                  }}>
                    {insights.sectorConcentration.score}
                  </p>
                  <span className="text-xs text-muted-foreground mb-1">/ 100</span>
                </div>
                <div className="w-full bg-muted rounded-full h-1.5 mt-2">
                  <div
                    className="h-1.5 rounded-full transition-all"
                    style={{
                      width: `${insights.sectorConcentration.score}%`,
                      backgroundColor: insights.sectorConcentration.score >= 70 ? "hsl(0, 80%, 50%)"
                        : insights.sectorConcentration.score >= 40 ? "hsl(35, 80%, 50%)"
                        : "hsl(142, 70%, 40%)"
                    }}
                  />
                </div>
                <p className="text-xs text-muted-foreground mt-1.5">
                  {insights.sectorConcentration.numSectors} sector{insights.sectorConcentration.numSectors !== 1 ? "s" : ""}
                  {insights.sectorConcentration.topSector && ` · Top: ${insights.sectorConcentration.topSector.name} (${insights.sectorConcentration.topSector.pct}%)`}
                </p>
                <p className="text-xs mt-0.5">
                  {insights.sectorConcentration.score >= 70 ? "🔴 High concentration risk" : insights.sectorConcentration.score >= 40 ? "🟡 Moderate concentration" : "🟢 Well spread across sectors"}
                </p>
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Most Correlated Pairs</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {insights.mostCorrelated.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="font-mono text-xs">{p.a} ↔ {p.b}</span>
                    <Badge variant="outline" className="text-xs" style={{ borderColor: getCorrelationColor(p.corr), color: getCorrelationColor(p.corr) }}>
                      {p.corr.toFixed(2)}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
            <Card>
              <CardHeader className="pb-2">
                <CardTitle className="text-sm font-medium text-muted-foreground">Least Correlated (Hedges)</CardTitle>
              </CardHeader>
              <CardContent className="space-y-1">
                {insights.leastCorrelated.map((p, i) => (
                  <div key={i} className="flex justify-between text-sm">
                    <span className="font-mono text-xs">{p.a} ↔ {p.b}</span>
                    <Badge variant="outline" className="text-xs" style={{ borderColor: getCorrelationColor(p.corr), color: getCorrelationColor(p.corr) }}>
                      {p.corr.toFixed(2)}
                    </Badge>
                  </div>
                ))}
              </CardContent>
            </Card>
          </div>
        )}

        {result && (
          <Tabs defaultValue="heatmap" className="w-full">
            <TabsList>
              <TabsTrigger value="heatmap" className="gap-1.5">
                <Grid3X3 className="h-3.5 w-3.5" /> Heatmap
              </TabsTrigger>
              <TabsTrigger value="network" className="gap-1.5">
                <Network className="h-3.5 w-3.5" /> Network Graph
              </TabsTrigger>
              <TabsTrigger value="dendrogram" className="gap-1.5">
                <GitBranch className="h-3.5 w-3.5" /> Dendrogram
              </TabsTrigger>
              <TabsTrigger value="clusters" className="gap-1.5">
                <Grid3X3 className="h-3.5 w-3.5" /> Cluster Analysis
              </TabsTrigger>
            </TabsList>

            <TabsContent value="heatmap">
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Correlation Heatmap</CardTitle>
                  <CardDescription>Pearson correlation of daily returns over {period === "3m" ? "3 months" : period === "6m" ? "6 months" : period === "1y" ? "1 year" : "2 years"}</CardDescription>
                </CardHeader>
                <CardContent className="overflow-x-auto">
                  <div className="inline-block">
                    {/* Legend */}
                    <div className="flex items-center gap-2 mb-4 text-xs text-muted-foreground">
                      <span>-1.0</span>
                      <div className="flex h-4 w-48 rounded overflow-hidden">
                        {[...Array(20)].map((_, i) => {
                          const v = -1 + (i / 19) * 2;
                          return <div key={i} className="flex-1" style={{ backgroundColor: getCorrelationColor(v) }} />;
                        })}
                      </div>
                      <span>+1.0</span>
                      <span className="ml-4">Blue = negative · Red = positive</span>
                    </div>

                    <table className="border-collapse">
                      <thead>
                        <tr>
                          <th className="p-1 text-xs font-medium text-muted-foreground sticky left-0 bg-background z-10" />
                          {result.tickers.map((t) => (
                            <th key={t} className="p-1">
                              <div className="text-[10px] font-mono font-medium text-muted-foreground -rotate-45 origin-bottom-left whitespace-nowrap w-8 h-16 flex items-end">
                                {t}
                              </div>
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {result.tickers.map((rowTicker, i) => (
                          <tr key={rowTicker}>
                            <td className="p-1 pr-2 text-[11px] font-mono font-medium text-muted-foreground sticky left-0 bg-background z-10 whitespace-nowrap">
                              {rowTicker}
                            </td>
                            {result.matrix[i].map((val, j) => (
                              <td key={j} className="p-0">
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <div
                                      className="w-10 h-10 flex items-center justify-center text-[10px] font-mono font-semibold cursor-default border border-background/50 transition-transform hover:scale-110 hover:z-10 hover:shadow-lg rounded-sm"
                                      style={{
                                        backgroundColor: getCorrelationColor(val),
                                        color: getTextColor(val),
                                      }}
                                    >
                                      {i === j ? "1" : val.toFixed(2)}
                                    </div>
                                  </TooltipTrigger>
                                  <TooltipContent side="top" className="text-xs">
                                    <p className="font-semibold">{result.tickers[i]} ↔ {result.tickers[j]}</p>
                                    <p>Correlation: {val.toFixed(3)} ({correlationLabel(val)})</p>
                                  </TooltipContent>
                                </Tooltip>
                              </td>
                            ))}
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                </CardContent>
              </Card>
            </TabsContent>

            <TabsContent value="network">
              <CorrelationNetworkGraph result={result} positions={positions} sectorMap={result.sectorMap} />
            </TabsContent>

            <TabsContent value="dendrogram">
              <CorrelationDendrogram result={result} sectorMap={result.sectorMap} />
            </TabsContent>

            <TabsContent value="clusters">
              <CorrelationClusterView result={result} sectorMap={result.sectorMap} />
            </TabsContent>
          </Tabs>
        )}

        {/* Empty state */}
        {!result && !loading && (
          <Card className="border-dashed">
            <CardContent className="p-12 text-center">
              <Grid3X3 className="h-12 w-12 mx-auto mb-4 text-muted-foreground/40" />
              <h3 className="text-lg font-semibold mb-2">No Correlation Data Yet</h3>
              <p className="text-sm text-muted-foreground max-w-md mx-auto">
                Import your portfolio or load from a saved snapshot, then click "Calculate Correlations" to generate the heatmap.
              </p>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
}
